import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useClassesStore } from '../../../stateManagers/store/useClassesStore';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../../../stateManagers/store/useDefaultDataStore';
import { useSchoolDetailsStore } from '../../../stateManagers/newStores/useSchoolDetailsStore';

interface Subject {
  id: string;
  subjectName: string;
  type: 'regular' | 'optional' | 'combined';
  mainSubjectId?: string | null;
}

interface SubjectForClass {
  subject_id: string;
  subject_name: string;
  class_id: string;
  class_name: string;
}

interface SubjectsResponse {
  status: string;
  message: string;
  subjects: SubjectForClass[];
}

interface EditRow {
  id: string;
  subjectName: string;
  subjectType: 'regular' | 'optional' | 'combined';
  mainSubjectId: string | null;
  error?: string;
}

interface EditSubjectModalProps {
  visible: boolean;
  subjects: Subject[];
  classId: number | null;
  onHide: () => void;
  onSaved: () => void;
}

const SUBJECT_TYPE_OPTIONS = [
  { label: 'Regular', value: 'regular' },
  { label: 'Optional', value: 'optional' },
  { label: 'Combined', value: 'combined' },
];

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  regular:  { bg: '#dbeafe', text: '#1e40af' },
  optional: { bg: '#fef3c7', text: '#92400e' },
  combined: { bg: '#e0e7ff', text: '#4338ca' },
};

export default function EditSubjectModal({
  visible,
  subjects,
  classId,
  onHide,
  onSaved,
}: EditSubjectModalProps) {
  const toast = useRef<Toast>(null);
  const { classes, fetchClasses } = useClassesStore();
  const activeSchool = useDefaultDataStore(state => state.active_school);
  const { schoolDetails } = useSchoolDetailsStore();

  const [rows, setRows] = useState<EditRow[]>([]);
  const [classSubjects, setClassSubjects] = useState<SubjectForClass[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentSession = schoolDetails?.current_session?.session || '';

  // Init rows whenever modal opens or subjects change
  useEffect(() => {
    if (visible && subjects.length > 0) {
      setRows(subjects.map(s => ({
        id: s.id,
        subjectName: s.subjectName,
        subjectType: s.type,
        mainSubjectId: s.mainSubjectId ?? null,
        error: undefined,
      })));
    }
  }, [visible, subjects]);

  // Fetch classes for dropdown
  useEffect(() => {
    if (visible && classes.length === 0) {
      const schoolId = activeSchool?.school_id;
      if (schoolId) {
        fetchClasses(schoolId);
      } else {
        fetchClasses();
      }
    }
  }, [visible, classes.length, fetchClasses, activeSchool?.school_id]);

  // Fetch all subjects of this class for main_subject_id options
  useEffect(() => {
    if (!visible || !classId) return;
    const fetchAll = async () => {
      setLoadingSubjects(true);
      try {
        const schoolId = activeSchool?.school_id;
        let url = `subjects_by_class_grouped?class_id=${classId}&flat=true`;
        if (schoolId) {
          url += `&school_id=${schoolId}`;
        }
        const response = await apiClient.get<SubjectsResponse>(url);
        setClassSubjects(response.data?.subjects ?? []);
      } catch {
        setClassSubjects([]);
      } finally {
        setLoadingSubjects(false);
      }
    };
    fetchAll();
  }, [visible, classId, activeSchool?.school_id]);

  const updateRow = (id: string, patch: Partial<EditRow>) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, ...patch, error: undefined };
      if (patch.subjectType && patch.subjectType !== 'combined') {
        updated.mainSubjectId = null;
      }
      return updated;
    }));
  };

  const handleSave = async () => {
    let hasError = false;
    const validated = rows.map(r => {
      if (!r.subjectName.trim()) {
        hasError = true;
        return { ...r, error: 'Name is required.' };
      }
      if (r.subjectType === 'combined' && !r.mainSubjectId) {
        hasError = true;
        return { ...r, error: 'Main subject required for combined.' };
      }
      return r;
    });

    if (hasError) {
      setRows(validated);
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Fix errors before saving.', life: 3000 });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        subjects: rows.map(r => ({
          id: r.id,
          subject_name: r.subjectName.trim(),
          subject_type: r.subjectType,
          class_id: classId,
          ...(r.subjectType === 'combined' ? { main_subject_id: r.mainSubjectId } : {}),
        })),
      };

      // Add school_id if available
      if (activeSchool?.school_id) {
        payload.school_id = activeSchool.school_id;
      }

      const response = await apiClient.put('/subjects/bulk', payload);
      const results: any[] = response.data?.results ?? [];

      const successCount = results.filter(r => r.status === 'success').length;
      const errorResults = results.filter(r => r.status === 'error');

      if (errorResults.length > 0) {
        setRows(prev => prev.map(r => {
          const match = errorResults.find((e: any) => e.id === r.id);
          return match ? { ...r, error: match.message } : r;
        }));
        toast.current?.show({
          severity: errorResults.length === results.length ? 'error' : 'warn',
          summary: 'Partial Update',
          detail: `${successCount} saved, ${errorResults.length} failed.`,
          life: 4000,
        });
        if (successCount > 0) onSaved();
      } else {
        toast.current?.show({
          severity: 'success',
          summary: 'Updated',
          detail: `${successCount} subject(s) updated successfully.`,
          life: 3000,
        });
        setTimeout(() => { onSaved(); onHide(); }, 400);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message ?? 'Something went wrong.',
        life: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleHide = () => {
    if (saving) return;
    onHide();
  };

  const headerContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: '10px',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <i className="pi pi-pencil" style={{ fontSize: '14px', color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
          {rows.length > 1 ? `Edit ${rows.length} Subjects` : 'Edit Subject'}
        </div>
        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '400', marginTop: '2px' }}>
          {rows.length > 1 ? 'All changes save in one request' : 'Update subject details below'}
        </div>
        {currentSession && (
          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
            Session: {currentSession}
          </div>
        )}
      </div>
    </div>
  );

  const footerContent = (
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '4px' }}>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleHide}
        disabled={saving}
        style={{
          background: 'transparent',
          border: '1.5px solid #e2e8f0',
          color: '#64748b',
          borderRadius: '10px',
          padding: '9px 18px',
          fontSize: '13px',
          fontWeight: '600',
        }}
      />
      <Button
        label={saving ? 'Saving...' : rows.length > 1 ? 'Save All' : 'Save Changes'}
        icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
        onClick={handleSave}
        disabled={saving}
        style={{
          background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
          border: 'none',
          borderRadius: '10px',
          padding: '9px 20px',
          fontSize: '13px',
          fontWeight: '600',
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.35)',
        }}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={handleHide}
        header={headerContent}
        footer={footerContent}
        style={{ width: '560px', borderRadius: '20px' }}
        contentStyle={{ padding: '20px 24px 8px', background: '#f8fafc' }}
        headerStyle={{ padding: '20px 24px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderRadius: '20px 20px 0 0' }}
        modal
        draggable={false}
        resizable={false}
        dismissableMask={!saving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '65vh', overflowY: 'auto', paddingRight: '2px' }}>
          {rows.map((row, index) => {
            const mainSubjectOptions = classSubjects
              .filter(s => s.subject_id !== row.id)
              .map(s => ({ label: s.subject_name, value: s.subject_id }));

            return (
              <div
                key={row.id}
                style={{
                  background: 'white',
                  borderRadius: '14px',
                  padding: '14px 16px',
                  border: row.error ? '1.5px solid #fca5a5' : '1.5px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {/* Row header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '700',
                    flexShrink: 0,
                  }}>
                    {index + 1}
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8' }}>ID #{row.id}</span>
                  {row.error && (
                    <span style={{ fontSize: '11px', color: '#ef4444', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <i className="pi pi-exclamation-circle" style={{ fontSize: '11px' }} />
                      {row.error}
                    </span>
                  )}
                </div>

                {/* Subject name */}
                <InputText
                  value={row.subjectName}
                  onChange={(e) => updateRow(row.id, { subjectName: e.target.value })}
                  placeholder="Subject name"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    border: '1.5px solid #e2e8f0',
                    padding: '8px 12px',
                    fontSize: '13px',
                    background: '#f8fafc',
                    color: '#1e293b',
                    boxSizing: 'border-box',
                  }}
                />

                {/* Type toggle */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  {SUBJECT_TYPE_OPTIONS.map((opt) => {
                    const colors = TYPE_COLORS[opt.value];
                    const isSelected = row.subjectType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => updateRow(row.id, { subjectType: opt.value as EditRow['subjectType'] })}
                        style={{
                          flex: 1,
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: isSelected ? '2px solid #2855ae' : '1.5px solid #e2e8f0',
                          background: isSelected ? colors.bg : 'white',
                          color: isSelected ? colors.text : '#94a3b8',
                          fontSize: '11px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          textTransform: 'capitalize',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Main subject — only for combined */}
                {row.subjectType === 'combined' && (
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Main Subject <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    {loadingSubjects ? (
                      <div style={{ fontSize: '12px', color: '#94a3b8', padding: '7px 12px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '12px' }} /> Loading...
                      </div>
                    ) : mainSubjectOptions.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#f59e0b', padding: '7px 12px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: '8px' }}>
                        No other subjects available.
                      </div>
                    ) : (
                      <Dropdown
                        value={row.mainSubjectId}
                        options={mainSubjectOptions}
                        onChange={(e) => updateRow(row.id, { mainSubjectId: e.value })}
                        placeholder="Select main subject"
                        style={{ width: '100%', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px' }}
                        panelStyle={{ borderRadius: '10px', fontSize: '13px' }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Dialog>
    </>
  );
}